// hooks/useFollowUp.js
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { toast } from "react-toastify";
import { authHeaders } from "../utils/session";

const headers = () => ({
  ...authHeaders(),
  "Content-Type": "application/json",
});

const multipartHeaders = () => ({
  ...authHeaders(),
});

// ── List follow-ups for a lead ───────────────────────────────────────────────
export const useLeadFollowUps = (leadId, filterType = "") => {
  return useQuery({
    queryKey: ["lead-followups", leadId, filterType],
    queryFn: async () => {
      /* lead_type=1 is what marks these rows as a lead's follow-ups rather than
         a customer's. Without it the API returns nothing for the lead — which is
         why the auto-generated demo invite never appeared here. */
      const params = { lead_id: leadId, lead_type: 1, limit: 50 };
      // "All" means no type filter at all, so every kind comes back.
      if (filterType && filterType !== "All") {
        params.type = filterType.toLowerCase();
      }
      const res = await axios.get(`${API_BASE_URL}/lead-followups`, {
        headers: headers(),
        params,
      });

      // console.log("from use-lead-follow-up", res);
      
      return res.data?.data || [];
    },
    enabled: !!leadId,
    keepPreviousData: true,
  });
};

// ── Create follow-up ─────────────────────────────────────────────────────────
/* The lead list is derived data: `kanban_column`, `outbound_followup_count` and
   `last_contacted_at` are all recomputed from the follow-ups table. Right after a
   write the list can still answer with the pre-write values, so one immediate
   refetch is not always enough — a second one a moment later settles it, and the
   user never has to reload the page. */
const refreshLeadLists = (qc) => {
  const keys = [
    "enquiryList",
    "inbox-leads",
    "future-leads",
    "done-leads",
    "unassigned-leads",
    "lead-details",
  ];
  keys.forEach((key) => qc.invalidateQueries({ queryKey: [key], refetchType: "all" }));
  setTimeout(() => {
    keys.forEach((key) => qc.refetchQueries({ queryKey: [key], type: "all" }));
  }, 1200);
};

export const useAddFollowUp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { attachments, ...rest } = payload;

      const hasFiles =
        Array.isArray(attachments) && attachments.length > 0 && attachments[0] instanceof File;

      if (hasFiles) {
        const fd = new FormData();
        Object.entries(rest).forEach(([k, v]) => {
          if (v !== "" && v !== null && v !== undefined) {
            fd.append(k, v);
          }
        });
        attachments.forEach((file, idx) => {
          fd.append(`attachments[${idx}]`, file, file.name);
        });

        const res = await axios.post(
          `${API_BASE_URL}/lead-followups`,
          fd,
          {
            headers: {
              ...authHeaders(),
            },
          }
        );
        if (res.data?.status === false) throw new Error(res.data.message);
        return { ...res.data, lead_id: rest.lead_id };
      }

      const clean = {};
      Object.entries(rest).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) clean[k] = v;
      });
      const res = await axios.post(
        `${API_BASE_URL}/lead-followups`,
        clean,
        { headers: headers() }
      );
      if (res.data?.status === false) throw new Error(res.data.message);
      return { ...res.data, lead_id: clean.lead_id };
    },
    onSuccess: (data) => {
      toast.success("Follow-up saved successfully");
      const lid = data.lead_id;
      qc.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "lead-followups" &&
          String(query.queryKey[1]) === String(lid),
      });
      refreshLeadLists(qc);
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to save follow-up");
    },
  });
};

// ── Update follow-up ─────────────────────────────────────────────────────────
// ── Update follow-up ─────────────────────────────────────────────────────────
export const useUpdateFollowUp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { id, lead_id, attachments, ...rest } = payload;

      const hasFiles =
        Array.isArray(attachments) && attachments.length > 0 && attachments[0] instanceof File;

      if (hasFiles) {
        const fd = new FormData();
        // Include lead_id in the form data
        if (lead_id) fd.append("lead_id", lead_id);
        Object.entries(rest).forEach(([k, v]) => {
          if (v !== "" && v !== null && v !== undefined) {
            fd.append(k, v);
          }
        });
        attachments.forEach((file, idx) => {
          fd.append(`attachments[${idx}]`, file, file.name);
        });

        const res = await axios.put(
          `${API_BASE_URL}/lead-followups/${id}`,
          fd,
          {
            headers: {
              ...authHeaders(),
            },
          }
        );
        if (res.data?.status === false) throw new Error(res.data.message);
        return { ...res.data, lead_id };
      }

      // Include lead_id in the JSON body
      const clean = {};
      if (lead_id) clean.lead_id = lead_id;
      Object.entries(rest).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) clean[k] = v;
      });
      const res = await axios.put(
        `${API_BASE_URL}/lead-followups/${id}`,
        clean,
        { headers: headers() }
      );
      if (res.data?.status === false) throw new Error(res.data.message);
      return { ...res.data, lead_id };
    },
    onSuccess: (data) => {
      toast.success("Follow-up updated");
      const lid = data.lead_id;
      qc.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "lead-followups" &&
          String(query.queryKey[1]) === String(lid),
      });
      refreshLeadLists(qc);
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to update follow-up");
    },
  });
};

// ── Delete follow-up ─────────────────────────────────────────────────────────
export const useDeleteFollowUp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, lead_id }) => {
      const res = await axios.delete(
        `${API_BASE_URL}/lead-followups/${id}`,
        { headers: headers() }
      );
      if (res.data?.status === false) throw new Error(res.data.message);
      return { ...res.data, lead_id };
    },
    onSuccess: (data) => {
      toast.success("Follow-up deleted");
      const lid = data.lead_id;
      qc.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "lead-followups" &&
          String(query.queryKey[1]) === String(lid),
      });
      refreshLeadLists(qc);
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to delete follow-up");
    },
  });
};

// ── Update lead stage status ─────────────────────────────────────────────────
export const useUpdateLeadStage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, follow_up_status, remarks }) => {
      const res = await axios.put(
        `${API_BASE_URL}/leads/${leadId}/stage`,
        { follow_up_status, remarks },
        { headers: headers() }
      );
      if (res.data?.status === false) throw new Error(res.data.message);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Lead stage updated");
      refreshLeadLists(qc);
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to update stage");
    },
  });
};